import { useCallback, useEffect, useState } from 'react';

import Language from '../helpers/Language';
import { EnabledLanguage } from '../interfaces/Language';
import { FCWithChild } from '../interfaces/Shared';
import { MultiLingualStylesheet, Stylesheet } from '../interfaces/Stylesheet';
import { getActiveStylesheet, getBrandColors } from '../services/StylesheetService';
import { StylesheetContext, StylesheetProviderStore } from './StylesheetContext';

const updateFavicon = (faviconUrl: string): void => {
  const link = document.createElement('link');
  const oldLink = document.getElementById('dynamic-favicon');
  link.id = 'dynamic-favicon';
  link.rel = 'shortcut icon';
  link.href = `${faviconUrl}?=${Math.random()}`;

  if (oldLink) {
    document.head.removeChild(oldLink);
  }
  document.head.appendChild(link);
};

const applyBrandColors = (stylesheet: Stylesheet): void => {
  const brandColors = getBrandColors(stylesheet);

  document.body.style.setProperty('--brand-primary', brandColors.primary);
  document.body.style.setProperty('--brand-secondary', brandColors.secondary);
  document.body.style.setProperty('--brand-background', brandColors.background);
};

interface StylesheetProviderProps {
  brandStylesheet: MultiLingualStylesheet | undefined;
  globalStylesheet: MultiLingualStylesheet;
}

const StylesheetProvider: FCWithChild<StylesheetProviderProps> = ({
  children,
  brandStylesheet,
  globalStylesheet
}) => {
  const [stylesheet, setUsedStylesheet] = useState<Stylesheet>(
    (brandStylesheet ? brandStylesheet : globalStylesheet)[Language.getLanguage()]
  );

  const changeStylesheetLanguage = (language: EnabledLanguage): void => {
    applyStylesheet(globalStylesheet, brandStylesheet, language);
  };

  const applyStylesheet = useCallback((
    globalStylesheet: MultiLingualStylesheet,
    brandStylesheet: MultiLingualStylesheet | undefined,
    language: EnabledLanguage
  ) => {
    const stylesheetWithTranslation = getActiveStylesheet(globalStylesheet, brandStylesheet, language);

    if (!stylesheetWithTranslation) {
      return;
    }

    setUsedStylesheet(stylesheetWithTranslation);

    document.title = stylesheetWithTranslation.settings.pageTitle;
    updateFavicon(stylesheetWithTranslation.settings.faviconUrl);
    applyBrandColors(stylesheetWithTranslation);
  }, []);

  useEffect(() => {
    applyStylesheet(globalStylesheet, brandStylesheet, Language.getLanguage());
  }, [globalStylesheet, brandStylesheet, applyStylesheet]);

  const store: StylesheetProviderStore = {
    stylesheet,
    globalStylesheet: globalStylesheet[Language.getLanguage()],
    changeStylesheetLanguage,
  };

  return (
    <StylesheetContext value={store}>
      {children}
    </StylesheetContext>
  );
};

export { StylesheetProvider };
